import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';

import { TradeBonusProgress } from '@flipster/features/src/earn-page/trade-bonus-progress';
import TradingVolumeRewardTiersModal from '@flipster/features/src/earn-page/trading-volume-reward-tiers/modal';
import { earnTradingBonusesBunja, getEarnTradingBonus } from '@flipster/features/src/shared/earn/state';
import { getTradePath } from '@flipster/features/src/shared/earn/trade-path';
import { OrderAttributionMapper, orderAttributionAtom } from '@flipster/features/src/shared/order-attribution';
import { useRouter } from '@flipster/i18n/routing';
import { useOverlay } from '@flipster/overlay';
import { Badge } from '@flipster/react/design-system/badge';
import { TextButton } from '@flipster/react/design-system/text-button';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { TradingBonusUsdtCurrencies, UsdtCurrency } from '@flipster/utils/constants/currency';
import { formatToShortNumber, numberFormatter } from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { UsdtPremiumEvent } from '@flipster/utils/segment/events/usdt-premium';

export const TradeBonusProgressSection = ({ currency }: { currency: string }) => {
  const overlay = useOverlay();
  const router = useRouter();
  const { earnTradingBonusesAtom } = useBunja(earnTradingBonusesBunja);

  const earnTradingBonuses = useAtomValue(earnTradingBonusesAtom);
  const {
    remainingFirstTradeVolume,
    firstTierTradeBonusProgress,
    tradeVolume,
    firstTierTradeVolume,
    nextLimitTierAprPct,
  } = getEarnTradingBonus(currency, earnTradingBonuses);
  const setOrderAttribution = useSetAtom(orderAttributionAtom);

  const tradingBonusUsdtCurrency = TradingBonusUsdtCurrencies.includes(currency) ? UsdtCurrency : currency;

  return (
    <div
      className={css({
        p: 16,
        textAlign: 'center',
        bg: 'background.default.level-0',
        color: 'content.default.level-4',
      })}
    >
      <p className={css({ textStyle: 'Text/L/Strong' })}>
        Trade {formatToShortNumber(remainingFirstTradeVolume.toString())} {tradingBonusUsdtCurrency} more to
      </p>
      <TextButton
        as='button'
        label='upgrade rewards'
        size='large'
        color='neutral'
        css={{ mb: 20 }}
        onClick={() =>
          overlay.open(
            <TradingVolumeRewardTiersModal
              buttonText='Trade more and upgrade'
              currency={currency}
              onClose={() => {
                segment.track(UsdtPremiumEvent.UsdtPremiumTradeClick);
                setOrderAttribution(OrderAttributionMapper.earn.earnTradingBonusNudge);
                router.push(getTradePath(currency));
              }}
            />,
          )
        }
      />
      <TradeBonusProgress css={css.raw({ w: '100%', mb: 8 })} progress={firstTierTradeBonusProgress} />
      <div className={hstack({ justify: 'space-between' })}>
        <p className={css({ textStyle: 'Text/S/Regular', textAlign: 'left' })}>
          {numberFormatter(tradeVolume.toFixed(0))} / {numberFormatter(firstTierTradeVolume.toFixed(0))}{' '}
          {tradingBonusUsdtCurrency}
        </p>
        <Badge color='neutral' type='tint'>
          +{numberFormatter(nextLimitTierAprPct.toString())}% APR
        </Badge>
      </div>
    </div>
  );
};
